/**
 * AcceptInvitePage - Public page for accepting invitation links from email.
 *
 * Reads ?token= from URL, calls the accept endpoint, shows result,
 * and redirects to /login on success.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

type AcceptState = 'loading' | 'success' | 'error';

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<AcceptState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    // M-5: Clear token from URL immediately to prevent leaking via browser history
    const url = new URL(window.location.href);
    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }

    if (!token) {
      setState('error');
      setErrorMessage('No invitation token found. Please check your invitation link.');
      return;
    }

    let cancelled = false;
    // M-6: Track timer for cleanup
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    async function accept() {
      try {
        const response = await api.userManagement.acceptInvitation(token!);
        if (cancelled) return;

        if (response.success) {
          setState('success');
          redirectTimer = setTimeout(() => {
            if (!cancelled) navigate('/login', { replace: true });
          }, 3000);
        } else {
          setState('error');
          setErrorMessage(response.error?.message || 'Failed to accept invitation');
        }
      } catch (err) {
        if (cancelled) return;
        setState('error');
        setErrorMessage(err instanceof Error ? err.message : 'Something went wrong');
      }
    }

    accept();
    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-sage-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden">
        <div className="bg-forest-800 text-white px-6 py-5">
          <h1 className="font-display text-xl font-semibold">TWG App Invitation</h1>
        </div>
        <div className="px-6 py-8 text-center">
          {state === 'loading' && (
            <>
              <div className="animate-spin h-8 w-8 border-4 border-forest-200 border-t-forest-700 rounded-full mx-auto mb-4" />
              <p className="text-sage-600">Accepting your invitation...</p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-forest-900 mb-2">You're in!</h2>
              <p className="text-sage-600 mb-4">
                Your invitation has been accepted. Redirecting to login...
              </p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="px-6 py-2.5 bg-forest-700 text-white rounded-xl font-medium hover:bg-forest-800 transition-colors"
              >
                Go to Login
              </button>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-forest-900 mb-2">Invitation Error</h2>
              <p className="text-sage-600 mb-4">{errorMessage}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="px-6 py-2.5 bg-sage-200 text-sage-700 rounded-xl font-medium hover:bg-sage-300 transition-colors"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
